export interface IMandatePlaceholders {
  /** Bank display name, e.g. "Chase". */
  institution: string;
  /** Last four of the account number. */
  mask: string;
  /** Server-formatted amount, e.g. "$99.00/year". */
  amount: string;
  /** Server-formatted first debit date, e.g. "September 24, 2026". */
  firstChargeDate: string;
}

/** Placeholder left unfilled — a template referencing something we cannot supply. */
export class UnfilledMandatePlaceholderError extends Error {
  constructor(placeholder: string) {
    super(`Mandate text references {${placeholder}}, which the renderer cannot supply`);
    this.name = 'UnfilledMandatePlaceholderError';
  }
}

/**
 * Fills a mandate template for display.
 *
 * Throws on a placeholder it cannot fill rather than rendering the braces.
 * This text is the authorization a subscriber reads and the version identifier
 * is recorded as evidence, so shipping `{amount}` verbatim would store a claim
 * that the subscriber saw terms nobody stated.
 */
export function interpolateMandate(template: string, values: Partial<IMandatePlaceholders>): string {
  // Partial by design: the card rail's template names no account, so callers
  // supply only what their copy interpolates — an unfilled placeholder still
  // throws below rather than rendering braces.
  const lookup: Partial<Record<string, string>> = { ...values };
  // Every braced token, not only word characters: a placeholder this matcher
  // skips is a placeholder that ships as literal braces, which is the exact
  // failure this function exists to refuse.
  return template.replace(/\{([^{}]+)\}/g, (_match, name: string) => {
    const value = lookup[name];
    if (value === undefined) {
      throw new UnfilledMandatePlaceholderError(name);
    }
    return value;
  });
}
